import React from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, UserPen, Flame, Video, Package, ArrowRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { howItWorksSteps, translations } from '../mock';
import { useData } from '../context/DataContext';
import PujaCard from './PujaCard';

const iconMap = { ClipboardList, UserPen, Flame, Video, Package };

function SectionHeading({ children, tag, subtitle }) {
  return (
    <div className="mb-8 md:mb-12 text-center">
      {tag && <span className="section-tag mb-2 devnag">{tag}</span>}
      <div className="section-heading">
        <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-nv-wine">{children}</span>
      </div>
      {subtitle && (
        <p className="text-center text-sm md:text-base text-nv-muted -mt-4 max-w-2xl mx-auto px-4">{subtitle}</p>
      )}
    </div>
  );
}

export function UpcomingPujas() {
  const { t, lang } = useLang();
  const { pujas } = useData();
  const items = pujas.slice(0, 4);
  return (
    <section id="pujas" className="py-14 md:py-20 px-4 sm:px-6 bg-nv-cream-soft">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          tag={lang === 'en' ? '✦ Our Popular Pujas ✦' : '✦ हमारी लोकप्रिय पूजाएं ✦'}
          subtitle={lang === 'en' ? 'Choose the right puja for every important moment of your life' : 'अपने जीवन के हर महत्वपूर्ण अवसर के लिए सही पूजा चुनें'}>
          {t(translations.sections.upcoming)}
        </SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((p) => (
            <PujaCard key={p.id} puja={p} />
          ))}
        </div>
        <div className="text-center mt-8 md:mt-10">
          <Link to="/upcoming-pujas" className="inline-flex items-center gap-2 btn-gold px-6 md:px-8 py-3 rounded-full font-semibold text-sm md:text-base">
            {t(translations.sections.viewAll)} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const { t, lang } = useLang();
  return (
    <section id="how" className="py-14 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-nv-cream-soft via-nv-cream-deep/60 to-nv-cream-soft">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          tag={lang === 'en' ? '✦ How It Works ✦' : '✦ कैसे काम करता है ✦'}
          subtitle={lang === 'en' ? 'Five simple steps from Sankalp to Prasad at your doorstep' : 'संकल्प से प्रसाद तक — पांच आसान चरण'}>
          {t(translations.sections.how)}
        </SectionHeading>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {howItWorksSteps.map((s, i) => {
            const Icon = iconMap[s.icon];
            return (
              <div key={s.id} className="relative bg-white rounded-2xl p-4 md:p-6 border border-nv-gold/20 shadow-sm hover:shadow-lg hover:-translate-y-1 transition text-center">
                <div className="absolute -top-2.5 -right-2.5 w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-nv-orange to-nv-orange-deep text-white text-xs font-bold flex items-center justify-center shadow-md">
                  {i + 1}
                </div>
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 rounded-full bg-gradient-to-br from-nv-cream-deep to-nv-cream-soft flex items-center justify-center border border-nv-gold/30">
                  <Icon className="w-6 h-6 md:w-7 md:h-7 text-nv-orange-deep" />
                </div>
                <h3 className="font-bold text-sm md:text-base text-nv-wine mb-1 md:mb-2">{lang === 'en' ? s.title_en : s.title_hi}</h3>
                <p className="text-xs md:text-sm text-nv-muted leading-relaxed">{lang === 'en' ? s.desc_en : s.desc_hi}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export { SectionHeading };
